<script>
import { useAlert, useTrack } from 'dashboard/composables';
import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import ReportFilterSelector from './components/FilterSelector.vue';
import { GROUP_BY_FILTER } from './constants';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import ReportContainer from './ReportContainer.vue';
import ConversationsListTable from './components/ConversationsListTable.vue';

const REPORTS_KEYS = {
  CONVERSATIONS: 'conversations_count',
  INCOMING_MESSAGES: 'incoming_messages_count',
  OUTGOING_MESSAGES: 'outgoing_messages_count',
  FIRST_RESPONSE_TIME: 'avg_first_response_time',
  RESOLUTION_TIME: 'avg_resolution_time',
  RESOLUTION_COUNT: 'resolutions_count',
  REPLY_TIME: 'reply_time',
};

export default {
  name: 'ConversationReports',
  components: {
    ReportFilterSelector,
    ReportContainer,
    ConversationsListTable,
  },
  data() {
    return {
      from: 0,
      to: 0,
      groupBy: GROUP_BY_FILTER[1],
      businessHours: false,
      conversationDirection: false,
      conversationsPageIndex: 0, // For conversations list pagination
    };
  },
  methods: {
    fetchAllData() {
      this.fetchAccountSummary();
      this.fetchChartData();
      this.fetchConversationsList(0); // Fetch conversations on first page
    },
    fetchAccountSummary() {
      try {
        this.$store.dispatch('fetchAccountSummary', this.getRequestPayload());
      } catch {
        useAlert(this.$t('REPORT.SUMMARY_FETCHING_FAILED'));
      }
    },
    fetchChartData() {
      [
        'CONVERSATIONS',
        'INCOMING_MESSAGES',
        'OUTGOING_MESSAGES',
        'FIRST_RESPONSE_TIME',
        'RESOLUTION_TIME',
        'RESOLUTION_COUNT',
        'REPLY_TIME',
      ].forEach(async key => {
        try {
          await this.$store.dispatch('fetchAccountReport', {
            metric: REPORTS_KEYS[key],
            ...this.getRequestPayload(),
          });
        } catch {
          useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
        }
      });
    },
    getRequestPayload() {
      const { from, to, groupBy, businessHours, conversationDirection } = this;
      return {
        from,
        to,
        groupBy: groupBy?.period,
        businessHours,
        conversationDirection,
      };
    },
    downloadAgentReports() {
      const { from, to } = this;
      const fileName = `agent-report-${format(
        fromUnixTime(to),
        'dd-MM-yyyy'
      )}.csv`;
      this.$store.dispatch('downloadAgentReports', { from, to, fileName });
    },
    onFilterChange({
      from,
      to,
      groupBy,
      businessHours,
      selectedConversationDirection,
    }) {
      this.from = from;
      this.to = to;
      this.groupBy = groupBy;
      this.businessHours = businessHours;
      this.conversationDirection = selectedConversationDirection.id;
      this.fetchAllData();

      useTrack(REPORTS_EVENTS.FILTER_REPORT, {
        filterValue: {
          from,
          to,
          groupBy,
          businessHours,
          selectedConversationDirection,
        },
        reportType: 'conversations',
      });
    },
    fetchConversationsList(page = 0) {
      const { from, to, groupBy, businessHours, conversationDirection } = this;
      this.$store.dispatch('fetchConversationsList', {
        metric: 'conversations_count',
        from,
        to,
        page: page + 1, // API uses 1-based pagination
        groupBy: groupBy?.period,
        businessHours,
        conversationDirection,
      });
    },
    onConversationsPageChange(pageIndex) {
      this.conversationsPageIndex = pageIndex;
      this.fetchConversationsList(pageIndex);
    },
  },
};
</script>

<template>
  <div class="flex-1 p-4 overflow-auto">
    <woot-button
      color-scheme="success"
      class-names="button--fixed-top"
      icon="arrow-download"
      @click="downloadAgentReports"
    >
      {{ $t('REPORT.DOWNLOAD_AGENT_REPORTS') }}
    </woot-button>
    <ReportFilterSelector
      :show-agents-filter="false"
      show-group-by-filter
      @filter-change="onFilterChange"
    />
    <ReportContainer :group-by="groupBy" />
    <!-- Conversations List Section -->
    <div class="mt-8">
      <div class="mb-4">
        <h3 class="text-xl font-semibold text-slate-800 dark:text-slate-100">
          {{ $t('CONVERSATIONS_TABLE.HEADER.TITLE') || 'Conversas' }}
        </h3>
        <p class="text-sm text-slate-600 dark:text-slate-400">
          {{
            $t('CONVERSATIONS_TABLE.DESCRIPTION') ||
            'Lista de conversas baseada nos filtros selecionados'
          }}
        </p>
      </div>
      <ConversationsListTable
        :page-index="conversationsPageIndex"
        @page-change="onConversationsPageChange"
      />
    </div>
  </div>
</template>
